"use client";

import { type PointerEvent, useEffect, useRef, useState } from "react";
import { ArrowUpRight, Expand, Pause, Play } from "lucide-react";
import { BLANK_IMAGE_SRC } from "@/lib/landing-constants";
import { HOMEPAGE_PROOF_RECORDS } from "@/lib/marketing/homepage-proof-records";
import styles from "./homepage-proof.module.css";

const formatAmount = (amount: number) => `+$${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function HomepageProfitProof({ motion, onOpen, hiddenOnPhones = false }: { motion: boolean; onOpen: (image: { src: string; title: string; source?: string }) => void; hiddenOnPhones?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const amountRef = useRef<HTMLSpanElement>(null);
  const elapsed = useRef(0);
  const [visible, setVisible] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [paused, setPaused] = useState(false);
  const [step, setStep] = useState(0);
  const record = HOMEPAGE_PROOF_RECORDS[step % HOMEPAGE_PROOF_RECORDS.length];

  useEffect(() => {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(preference.matches);
    update();
    preference.addEventListener("change", update);
    return () => preference.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting && entry.intersectionRatio >= .5), { threshold: .5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (paused || hovered || focused || !motion) {
      if (visible && !reduced) {
        const slot = Math.floor(elapsed.current / 3000);
        elapsed.current = Math.max(elapsed.current, slot * 3000 + 2800);
        if (amountRef.current) amountRef.current.textContent = formatAmount(HOMEPAGE_PROOF_RECORDS[slot % HOMEPAGE_PROOF_RECORDS.length].amount);
      }
      return;
    }
    if (!visible || reduced) return;
    let frame = 0;
    const start = performance.now() - elapsed.current;
    const tick = (now: number) => {
      const previous = Math.floor(elapsed.current / 3000);
      elapsed.current = Math.max(0, now - start);
      const next = Math.floor(elapsed.current / 3000);
      const progress = Math.min(Math.max((elapsed.current - next * 3000 - 400) / 2400, 0), 1);
      const { amount } = HOMEPAGE_PROOF_RECORDS[next % HOMEPAGE_PROOF_RECORDS.length];
      if (next !== previous) setStep(next);
      if (amountRef.current) amountRef.current.textContent = formatAmount(Math.round(amount * 100 * (1 - (1 - progress) ** 3)) / 100);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [focused, hovered, motion, paused, reduced, visible]);

  useEffect(() => {
    if (reduced && amountRef.current) amountRef.current.textContent = formatAmount(record.amount);
  }, [reduced, record]);

  const updateHover = ({ pointerType, target }: PointerEvent) => { if (pointerType !== "touch" && target instanceof Element && !target.closest("[data-playback-control]")) setHovered(true); };

  return <div ref={ref} className={styles.heroProof} data-paused={paused || undefined} onPointerEnter={updateHover} onPointerMove={updateHover} onPointerLeave={() => setHovered(false)}>
    <svg className={styles.orbits} viewBox="0 0 600 600" fill="none" aria-hidden="true">{[180, 210, 240, 270, 300].map((r) => <circle key={r} cx="300" cy="300" r={r} />)}</svg>
    {!reduced && motion && <button type="button" data-playback-control className={`${styles.iconButton} ${styles.proofPause}`} onClick={() => { setPaused(!paused); setHovered(false); }} aria-label={paused ? "Resume receipts" : "Pause receipts"}>{paused ? <Play size={15} /> : <Pause size={15} />}</button>}
    <div className={styles.proofStage}>
      <button type="button" className={styles.proofButton} onClick={() => onOpen(record)} onFocus={(event) => setFocused(event.currentTarget.matches(":focus-visible"))} onBlur={() => setFocused(false)} aria-label={`Open ${record.name}’s ${record.period} receipt`}>{HOMEPAGE_PROOF_RECORDS.map((item, index) => <span key={item.id} className={`${styles.proofImage} ${item === record ? styles.proofFlyIn : styles.proofWaiting}`} aria-hidden={item !== record || undefined}>{hiddenOnPhones ? <picture><source media="(max-width: 767px)" srcSet={BLANK_IMAGE_SRC} /><img src={item.src} width={item.width} height={item.height} alt={item.title} fetchPriority={index === 0 ? "high" : "low"} /></picture> : <img src={item.src} width={item.width} height={item.height} alt={item.title} fetchPriority={index === 0 ? "high" : "low"} />}<span className={styles.expand}><Expand size={15} /> View original</span></span>)}</button>
    </div>
    <div className={styles.profitBadge}>
      <div className={styles.profitLabel}>{record.name} · {record.metric}</div>
      <strong className="data-hero" aria-label={`${record.name}: $${record.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })} ${record.metric.toLowerCase()}`}><span ref={amountRef} aria-hidden="true">{formatAmount(HOMEPAGE_PROOF_RECORDS[0].amount)}</span><ArrowUpRight size={24} aria-hidden="true" /></strong>
      <small>{record.period}</small>
    </div>
    <p className={styles.proofCaption}>Original personal results · Different periods · Not typical or guaranteed.</p>
  </div>;
}
